import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Send, 
  Clock, 
  CheckCircle, 
  ExternalLink,
  Building2,
  Mail,
  User,
  Phone,
  Users,
  UserPlus
} from 'lucide-react';
import { toast } from 'react-toastify';
import { escalateTicketToSupplier, getTicketEscalationHistory, getSuppliers } from '../../../../models/helpdeskModel';
import { useAuth } from '../../../../context/AuthContext';
import Button from '../../../../components/common/Button';
import WysiwygEditor from '../../../../components/common/WysiwygEditor';

const SupplierEscalationTab = ({ ticket, selectedSupplier, onTicketUpdate }) => {
  const { isIT } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [escalationHistory, setEscalationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  
  // Form data for escalation
  const [escalationForm, setEscalationForm] = useState({
    supplier_id: '',
    contact_email: '',
    subject: '',
    message: '',
    cc: '',
    priority: 'normal'
  });

  useEffect(() => {
    if (ticket) {
      fetchSuppliers();
      fetchEscalationHistory();
      
      // Pre-populate subject with ticket number for proper email threading
      const ticketNumber = ticket.ticket_number || ticket.number || `#${ticket.id || ticket.ticket_id}`;
      const ticketSubject = ticket.subject || ticket.ticket_subject || ticket.title || 'Support Request';
      
      setEscalationForm(prev => ({
        ...prev,
        subject: `${ticketNumber} - ${ticketSubject}`
      }));
    }
  }, [ticket]);

  // Handle selectedSupplier from the escalation dropdown
  useEffect(() => {
    if (selectedSupplier) {
      // Get primary email (To field) - from E_Mail
      const primaryEmail = selectedSupplier.supplier_email || '';
      
      // Get CC emails from supplier users
      const ccEmails = selectedSupplier.supplier_cc_emails || [];
      const ccEmailString = ccEmails.join(', ');
      
      setEscalationForm(prev => ({
        ...prev,
        supplier_id: selectedSupplier.supplier_id || selectedSupplier.BP_Code,
        contact_email: primaryEmail,
        cc: ccEmailString,
        message: ''  // Start with empty message for user to type their collaboration message
      }));
      
      // Show the escalation form immediately
      setShowEscalationForm(true);
    }
  }, [selectedSupplier]);

  const fetchSuppliers = async () => {
    try {
      const response = await getSuppliers();
      
      // Handle the response structure
      let suppliersData = [];
      if (response.status === 'success' && response.data) {
        suppliersData = response.data;
      } else if (response.data) {
        suppliersData = Array.isArray(response.data) ? response.data : [];
      }
      
      // Filter suppliers that have contact information and a valid name
      const suppliersWithContacts = suppliersData.filter(supplier => {
        // Check if supplier has a valid name (BP_Name)
        const hasName = supplier.BP_Name && supplier.BP_Name.trim() !== '' && supplier.BP_Name !== 'null';
        
        // Check for any contact method (email, phone, mobile)
        const hasContact = (supplier.Email && supplier.Email.trim() !== '' && supplier.Email.includes('@')) ||
                          (supplier.Contact_Email && supplier.Contact_Email.trim() !== '' && supplier.Contact_Email.includes('@')) ||
                          (supplier.Mobile_Phone && supplier.Mobile_Phone.trim() !== '') ||
                          (supplier.Contact_Person && supplier.Contact_Person.trim() !== '');
        
        return hasName && hasContact;
      });
      
      // Transform the suppliers to have consistent field names for the UI
      const transformedSuppliers = suppliersWithContacts.map(supplier => ({
        supplier_id: supplier.BP_Code,
        supplier_name: supplier.BP_Name,
        supplier_email: supplier.Email || supplier.Contact_Email || '',
        contact_person: supplier.Contact_Person || '',
        mobile_phone: supplier.Mobile_Phone || '',
        group_code: supplier.Group_Code || ''
      }));
      
      setSuppliers(transformedSuppliers);
      
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to load suppliers');
    }
  };

  const fetchEscalationHistory = async () => {
    try {
      setLoading(true);
      const response = await getTicketEscalationHistory(ticket.id || ticket.ticket_id);
      if (response.status === 1 && Array.isArray(response.escalations)) {
        // Filter for supplier collaborators
        const supplierCollaborators = response.escalations.filter(e => 
          e.escalation_type === 'supplier_collaborator' || e.escalation_type === 'supplier'
        );
        setEscalationHistory(supplierCollaborators);
      }
    } catch (error) {
      console.error('Error fetching escalation history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierSelect = (supplierId) => {
    if (supplierId) {
      // Find the selected supplier
      const supplier = suppliers.find(s => s.supplier_id == supplierId);
      
      if (supplier) {
        setEscalationForm(prev => ({
          ...prev,
          supplier_id: supplierId,
          contact_email: supplier.supplier_email,
          cc: supplier.supplier_contact_email || ''
        }));
      } else {
        // Supplier not found, clear contact fields but keep supplier_id
        setEscalationForm(prev => ({
          ...prev,
          supplier_id: supplierId,
          contact_email: '',
          cc: ''
        }));
      }
    } else {
      // No supplier selected, clear all supplier-related fields
      setEscalationForm(prev => ({
        ...prev,
        supplier_id: '',
        contact_email: '',
        cc: ''
      }));
    }
  };

  const handleEscalate = async () => {
    if (!escalationForm.supplier_id || !escalationForm.contact_email || !escalationForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setEscalating(true);
      const response = await escalateTicketToSupplier(ticket.id || ticket.ticket_id, {
        supplier_id: escalationForm.supplier_id,
        contact_email: escalationForm.contact_email,
        subject: escalationForm.subject,
        message: escalationForm.message,
        cc: escalationForm.cc,
        priority: escalationForm.priority
      });

      if (response.status === 1) {
        toast.success('Supplier added as collaborator successfully');
        setShowEscalationForm(false);
        
        // Reset form with proper ticket number format for future escalations
        const ticketNumber = ticket.ticket_number || ticket.number || `#${ticket.id || ticket.ticket_id}`;
        const ticketSubject = ticket.subject || ticket.ticket_subject || ticket.title || 'Support Request';
        
        setEscalationForm({
          supplier_id: '',
          contact_email: '',
          subject: `${ticketNumber} - ${ticketSubject}`,
          message: '',
          cc: '',
          priority: 'normal'
        });
        fetchEscalationHistory();
        if (onTicketUpdate) {
          onTicketUpdate();
        }
      } else {
        toast.error(response.message || 'Failed to add supplier as collaborator');
      }
    } catch (error) {
      console.error('Error adding supplier collaborator:', error);
      toast.error('Failed to add supplier as collaborator');
    } finally {
      setEscalating(false);
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  if (!isIT) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-gray-600">Only IT staff can escalate tickets to suppliers.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Building2 className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Supplier Escalation</h3>
        </div>
        
        {!showEscalationForm && (
          <Button
            onClick={() => setShowEscalationForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            <Users className="w-4 h-4 mr-2" />
            Add Supplier Collaborator
          </Button>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Building2 className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Supplier Collaboration</p>
            <p>Grant a supplier access to collaborate on this ticket. The supplier will receive notification and can view/comment on the ticket while IT maintains primary ownership.</p>
          </div>
        </div>
      </div>

      {/* Escalation Form */}
      {showEscalationForm && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">Add Supplier Collaborator</h4>
            <Button
              onClick={() => setShowEscalationForm(false)}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
          </div>

          <div className="space-y-4">
            {/* Supplier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier Collaborator *
              </label>
              <select
                value={escalationForm.supplier_id}
                onChange={(e) => handleSupplierSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                required
              >
                <option value="">Select a supplier to collaborate...</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.supplier_name}
                  </option>
                ))}
              </select>
              {suppliers.length === 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  No suppliers with contact information found.
                </p>
              )}
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email *
              </label>
              <input
                type="email"
                value={escalationForm.contact_email}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                placeholder="supplier.support@company.com"
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={escalationForm.subject}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                placeholder="Enter escalation subject"
                required
              />
            </div>

            {/* CC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC (Optional)
              </label>
              <input
                type="text"
                value={escalationForm.cc}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, cc: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                placeholder="cc@example.com, another@example.com"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={escalationForm.priority}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Collaboration Message *
              </label>
              <WysiwygEditor
                value={escalationForm.message}
                onChange={(content) => setEscalationForm(prev => ({ ...prev, message: content }))}
                placeholder="Enter your collaboration message. This will be saved as an internal reply and included in the notification to the supplier..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowEscalationForm(false)}
                variant="outline"
                disabled={escalating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEscalate}
                disabled={escalating}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {escalating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding Collaborator...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Supplier Collaborator
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Collaborators */}
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-4">Supplier Collaborators</h4>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading collaborators...</span>
          </div>
        ) : escalationHistory.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No supplier collaborators yet</p>
            <p className="text-sm text-gray-500 mt-1">Supplier collaborators will appear here once added</p>
          </div>
        ) : (
          <div className="space-y-4">
            {escalationHistory.map((escalation, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-gray-900">
                      Added collaborator: {escalation.supplier_name || 'Supplier'}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDateTime(escalation.created_at)}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{escalation.contact_email}</span>
                  </div>
                  {escalation.subject && (
                    <div className="flex items-start space-x-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <span className="text-xs">📋</span>
                      </div>
                      <span>Subject: {escalation.subject}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                  <div dangerouslySetInnerHTML={{ __html: escalation.message }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierEscalationTab;